import React, { useContext, useEffect, useState } from "react";
import { Box, styled } from "@mui/material";
import { CheckCircle, AlertTriangle, X, Loader2 } from "lucide-react";
import { ThemeContext } from "../ThemeContext";

interface AlertMessageProps {
  type: "success" | "error" | "loading";
  message: string;
  onClose?: () => void;
  autoHideDuration?: number;
}

interface StyledProps {
  $type: "success" | "error" | "loading";
  $isDark: boolean;
}

const AlertContainer = styled(Box)<StyledProps>(({ $type, $isDark }) => ({
  display: "flex",
  alignItems: "center",
  padding: "12px 16px",
  borderRadius: "8px",
  marginBottom: "16px",
  backgroundColor: 
    $type === "success" 
      ? $isDark ? "rgba(34, 197, 94, 0.15)" : "rgba(34, 197, 94, 0.1)" 
      : $type === "error" 
      ? $isDark ? "rgba(239, 68, 68, 0.15)" : "rgba(239, 68, 68, 0.1)"
      : $isDark ? "rgba(96, 165, 250, 0.15)" : "rgba(96, 165, 250, 0.1)",
  border: `1px solid ${
    $type === "success" 
      ? "#22C55E" 
      : $type === "error" 
      ? "#EF4444"
      : "#60A5FA"
  }`,
  animation: "fadeIn 0.3s ease-in-out",
  "@keyframes fadeIn": {
    "0%": {
      opacity: 0,
      transform: "translateY(-10px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

const IconContainer = styled(Box)<{ $type: "success" | "error" | "loading" }>(({ $type }) => ({
  marginRight: "12px",
  color: 
    $type === "success" 
      ? "#22C55E" 
      : $type === "error" 
      ? "#EF4444"
      : "#60A5FA",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& .rotating": {
    animation: "spin 2s linear infinite",
  },
  "@keyframes spin": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },
}));

const CloseButton = styled(Box)<{ $isDark: boolean }>(({ $isDark }) => ({
  marginLeft: "auto",
  cursor: "pointer",
  color: $isDark ? "#A1A1AA" : "#9CA3AF",
  "&:hover": {
    color: $isDark ? "#FFFFFF" : "#1F2937",
  },
}));

const AlertMessage: React.FC<AlertMessageProps> = ({ 
  type, 
  message, 
  onClose,
  autoHideDuration = 5000,
}) => {
  const { isDarkMode } = useContext(ThemeContext);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (type === "success" && autoHideDuration > 0) {
      const timer = setTimeout(() => {
        setVisible(false);
        if (onClose) onClose();
      }, autoHideDuration);
      
      return () => clearTimeout(timer);
    }
  }, [type, autoHideDuration, onClose]);

  if (!visible) return null;

  return (
    <AlertContainer $type={type} $isDark={isDarkMode}>
      <IconContainer $type={type}>
        {type === "success" ? (
          <CheckCircle size={20} />
        ) : type === "error" ? (
          <AlertTriangle size={20} />
        ) : (
          <Loader2 size={20} className="rotating" />
        )}
      </IconContainer>
      
      <Box 
        sx={{ 
          fontSize: "14px", 
          color: isDarkMode ? "#FFFFFF" : "#1F2937" 
        }}
      >
        {message}
      </Box>
      
      {type !== "loading" && onClose && (
        <CloseButton $isDark={isDarkMode} onClick={onClose}>
          <X size={16} />
        </CloseButton>
      )}
    </AlertContainer>
  );
};

export default AlertMessage;